import React, { useEffect } from "react";
import "@/App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import LandingPage from "./pages/LandingPage";
import HoldingPage from "./pages/HoldingPage";
import LoginPage from "./pages/LoginPage";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import ProfileHome from "./pages/ProfileHome";
import ChapterInterview from "./pages/ChapterInterview";
import StoriesFeed from "./pages/StoriesFeed";
import BookPreview from "./pages/BookPreview";
import OrderPage from "./pages/OrderPage";
import OrderSuccess from "./pages/OrderSuccess";
import SubscribePage from "./pages/SubscribePage";
import GiftPage from "./pages/GiftPage";
import SubscribeSuccess from "./pages/SubscribeSuccess";
import FamilyTree from "./pages/FamilyTree";
import StoryWeekWelcome from "./pages/StoryWeekWelcome";
import StoryWeekDay from "./pages/StoryWeekDay";
import StoryWeekComplete from "./pages/StoryWeekComplete";
import StoryWeekSetup from "./pages/StoryWeekSetup";
import VerifyEmail from "./pages/VerifyEmail";
import FoundingBeta from "./pages/FoundingBeta";
import FromTheBook from "./pages/FromTheBook";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Soundtrack from "./pages/Soundtrack";
import MyLivingBook from "./pages/MyLivingBook";
import DeliverySettings from "./pages/DeliverySettings";
import MyBook from "./pages/MyBook";
import AdminWaitlist from "./pages/AdminWaitlist";
import AdminUsers from "./pages/AdminUsers";
import DesignPreviewConversationCream from "./pages/DesignPreviewConversationCream";
import DesignPreviewConversationDark from "./pages/DesignPreviewConversationDark";
import DesignPreviewConsent from "./pages/DesignPreviewConsent";
import OnboardingStoryteller from "./pages/OnboardingStoryteller";
import OnboardingWho from "./pages/OnboardingWho";
import StoryWeekIntro from "./pages/StoryWeekIntro";
import ConversationScreen from "./pages/ConversationScreen";
import ConversationReview from "./pages/ConversationReview";
// Public story-sharing paused for launch — SharedStory not routed.
import CookieBanner from "./components/CookieBanner";

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    // Whenever the route path changes, open the next page at the top.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center paper">
        <p className="italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.3rem" }}>
          Reflecting on your story…
        </p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<HoldingPage />} />
      <Route path="/_preview-landing" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/admin/waitlist" element={<ProtectedRoute><AdminWaitlist /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
      <Route path="/design-preview/conversation-cream" element={<DesignPreviewConversationCream />} />
      <Route path="/design-preview/conversation-dark" element={<DesignPreviewConversationDark />} />
      <Route path="/design-preview/consent" element={<DesignPreviewConsent />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/my-living-book" element={<ProtectedRoute><MyLivingBook /></ProtectedRoute>} />
      <Route path="/my-book" element={<ProtectedRoute><MyBook /></ProtectedRoute>} />
      <Route path="/settings/delivery" element={<ProtectedRoute><DeliverySettings /></ProtectedRoute>} />
      <Route path="/app/profile/:id" element={<ProtectedRoute><ProfileHome /></ProtectedRoute>} />
      <Route path="/app/chapter/:profileId/:chapterId" element={<ProtectedRoute><ChapterInterview /></ProtectedRoute>} />
      <Route path="/app/stories/:profileId" element={<ProtectedRoute><StoriesFeed /></ProtectedRoute>} />
      <Route path="/app/book/:profileId" element={<ProtectedRoute><BookPreview /></ProtectedRoute>} />
      <Route path="/app/order/:profileId" element={<ProtectedRoute><OrderPage /></ProtectedRoute>} />
      <Route path="/order/success" element={<ProtectedRoute><OrderSuccess /></ProtectedRoute>} />
      <Route path="/subscribe" element={<ProtectedRoute><SubscribePage /></ProtectedRoute>} />
      <Route path="/gift" element={<ProtectedRoute><GiftPage /></ProtectedRoute>} />
      <Route path="/subscribe/success" element={<ProtectedRoute><SubscribeSuccess kind="subscription" /></ProtectedRoute>} />
      <Route path="/gift/success" element={<ProtectedRoute><SubscribeSuccess kind="gift" /></ProtectedRoute>} />
      <Route path="/app/tree" element={<ProtectedRoute><FamilyTree /></ProtectedRoute>} />
      {/* Story Week™ — free entry point (authed users only, from setup onward) */}
      <Route path="/story-week" element={<StoryWeekWelcome />} />
      <Route path="/story-week/setup" element={<StoryWeekSetup />} />
      <Route path="/story-week/intro" element={<ProtectedRoute><StoryWeekIntro /></ProtectedRoute>} />
      <Route path="/story-week/day/:day" element={<StoryWeekDay />} />
      <Route path="/story-week/day/:day/conversation" element={<ProtectedRoute><ConversationScreen /></ProtectedRoute>} />
      <Route path="/story-week/day/:day/review" element={<ProtectedRoute><ConversationReview /></ProtectedRoute>} />
      <Route path="/story-week/complete" element={<StoryWeekComplete />} />
      <Route path="/onboarding/storyteller" element={<ProtectedRoute><OnboardingWho /></ProtectedRoute>} />
      <Route path="/onboarding/storyteller/details" element={<ProtectedRoute><OnboardingStoryteller /></ProtectedRoute>} />
      <Route path="/auth/verify" element={<VerifyEmail />} />
      <Route path="/founding-beta" element={<FoundingBeta />} />
      <Route path="/from-the-book" element={<FromTheBook />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/app/soundtrack/:profileId" element={<ProtectedRoute><Soundtrack /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppRouter />
          <CookieBanner />
          <Toaster richColors position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
